<template>
  <div v-if="downloadLinks.length > 0 || googleDriveLinks.length > 0">
    <A11yDropdown
      v-if="!downloadPermissions.block && downloadLinks.length > 0"
      :dropdown-props="{
        ref:'downloadDropdown'
      }"
      :button-props="{
        type:'button',
        class:'btn btn-primary dropdown-toggle download-btn' + (condenseButtons ? ' condense-buttons' : ''),
        role:'combobox',
        'aria-haspopup':'listbox',
        id:'download-dropdown-button-'+this.id,
        'aria-controls':'download-dropdown-menu-'+this.id,
        'aria-label':`Download: ${this.resource.title} resources`,
        'tooltipText': condenseButtons ? 'Download' : ''
      }"
      :dom-attributes="{
        role:'listbox',
        id:'download-dropdown-menu-'+this.id
      }"
      @openedDropdown="trackDownloadsShown($event)"
      @tooltipShow="$emit('tooltipShow')"
      @tooltipHide="$emit('tooltipHide')">
      <template slot="button-contents">
        <span class="material-icons-outlined pr-1" :class="condenseButtons ? 'large-icons' : 'small-icons'">file_download</span>
        <template v-if="!condenseButtons">
          <span>Download</span>
          <span class="material-icons-round">arrow_drop_down</span>
        </template>
      </template>
      <template slot="dropdown-items">
        <li v-for="link in downloadLinks" :key="link.label">
          <a role="button" @click="validateAndOpenDownload(link, false)">
            Download {{ formatLabelWithSet(link.label) }}
          </a>
        </li>
      </template>
    </A11yDropdown>

    <A11yDropdown
      v-if="!downloadPermissions.block && googleDriveLinks.length > 0"
      :dropdown-props="{ref:'googleDropdown'}"
      :button-props="{
        type:'button',
        class:'btn btn-gb-light dropdown-toggle' + (condenseButtons ? ' condense-buttons' : ''),
        role:'combobox',
        'aria-haspopup':'listbox',
        id:'google-dropdown-button-'+this.id,
        'aria-controls':'google-dropdown-menu-'+this.id,
        'aria-label':`Open with Google: ${this.resource.title} resource`,
        'tooltipText': condenseButtons ? 'Open with Google' : ''
      }"
      :dom-attributes="{
        role:'listbox',
        id:'google-dropdown-menu-'+this.id
      }"
      @openedDropdown="trackGoogleLinksShown($event)"
      @tooltipShow="$emit('tooltipShow')"
      @tooltipHide="$emit('tooltipHide')"
    >
      <template slot="button-contents">
        <span class="material-icons-round pr-2" :class="condenseButtons ? 'large-icons' : 'small-icons'">add_to_drive</span>
        <template v-if="!condenseButtons">
          <span>Open With Google</span>
          <span class="material-icons-round">arrow_drop_down</span>
        </template>
      </template>
      <template slot="dropdown-items">
        <li v-for="link in googleDriveLinks" :key="link.label">
          <a
            class="open-in-drive-dropdown"
            role="button"
            @click="validateAndOpenDownload(link, true)"
          >
            {{ formatLabelWithSet(link.label) }} <span v-if="link.link_type"> ({{ capitalizeLinkType(link.link_type) }}) </span>
          </a>
        </li>
      </template>
    </A11yDropdown>

    <template v-if="downloadPermissions.block">
      <tooltip
        trigger="outside-click"
        placement="top"
        :text="downloadBlockedTooltipText"
        v-model="downloadBlockedTooltipOpen"
        v-if="!downloadPermissions.blockUnauthenticated">
        <button
          type="button"
          class="btn btn btn-primary d-inline-flex align-center"
          :aria-expanded="downloadBlockedTooltipOpen ? 'true' : 'false'"
          @click="contentBlocked">
          <span class="m-xxs">
            {{ downloadBlockedButtonText }}
          </span>
        </button>
      </tooltip>

      <tooltip
        trigger="outside-click"
        placement="top"
        :text="downloadBlockedTooltipText"
        v-model="googleBlockedTooltipOpen"
        v-if="!downloadPermissions.blockUnauthenticated && googleDriveLinks">
        <button
          type="button"
          class="btn btn-gb-light d-inline-flex align-center"
          :aria-expanded="googleBlockedTooltipOpen ? 'true' : 'false'"
          @click="contentBlocked">
          <span class="material-icons-round pr-1">add_to_drive</span>
          <span class="m-xxs">
            {{ downloadBlockedButtonText }}
          </span>
        </button>
      </tooltip>

      <template v-if="downloadPermissions.blockUnauthenticated && showUnauthenticated">
        <a :href="signInReturn" class="btn btn-primary btn-sm">Sign in</a>
        <a class="btn btn-default btn-sm" :href="signUpReturn">Create an account</a>
      </template>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { tooltip } from 'uiv'
import { urlHelpers } from '@/links'
import { actionPermissions, ACTION_TYPES, CONTENT_TYPES } from '@/util/access_visibility'
import A11yDropdown from '@/components/A11yDropdown'

export default {
  name: 'ResourceDownloadButton',
  directives: { tooltip },
  components: { A11yDropdown },
  props: {
    resource: {
      type: Object,
      required: true
    },
    showUnauthenticated: {
      type: Boolean,
      required: false,
      default: true
    },
    newHomepage: {
      type: Boolean,
      required: false,
      default: false
    },
    condenseButtons: {
      type: Boolean,
      required: false,
      default: false
    },
    evtData: {
      type: Object,
      default: () => {}
    }
  },
  data: function() {
    return {
      id: null,
      downloadBlockedTooltipOpen: false,
      googleBlockedTooltipOpen: false
    }
  },
  eventContext() {
    return {
      resource_uuid: this.resource.uuid,
      resource_title: this.resource.title,
      resource_type: this.resource.type,
      resource_access: !this.downloadPermissions.block,
      resource_download_count: this.downloadCount,
      content_access_report_error: this.contentAccessStatus.reportError,
      resource_language: this.resource.language,
      ...this.evtData,

      //Deprecated
      uuid: this.resource.uuid,
      title: this.resource.title,
      access: !this.downloadPermissions.block
    }
  },
  computed: {
    ...mapGetters('contentAccess', ['contentAccessStatus', 'downloadQuota', 'downloadCount']),
    ...mapGetters('user', ['authInfo']),
    downloadLinks() {
      return this.resource.download_links || []
    },
    googleDriveLinks() {
      return this.resource.google_drive_links || []
    },
    downloadPermissions() {
      return actionPermissions(
        ACTION_TYPES.DOWNLOAD,
        CONTENT_TYPES.RESOURCE,
        this.resource,
        this.authInfo,
        this.contentAccessStatus
      )
    },
    downloadBlockedButtonText() {
      return (
        (this.downloadPermissions.blockExpired && 'Membership expired') ||
        (this.downloadPermissions.blockQuota && 'Download quota exceeded')
      )
    },
    downloadBlockedTooltipText() {
      if (this.downloadPermissions.blockExpired)
        return 'To download this resource, you must have an active membership through your school or district. Contact your administrator to purchase or renew your premium membership.'
      if (this.downloadPermissions.blockQuota)
        return `You have already used all of your available downloads for this month.`
    },
    signInReturn() {
      return urlHelpers.signInReturn(this.$store.state.currentUrl)
    },
    signUpReturn() {
      return urlHelpers.signUpReturn('toolkit', this.$store.state.currentUrl)
    },
    contentInfo() {
      return {
        cu: this.resource.uuid,
        ct: this.resource.title
      }
    }
  },
  methods: {
    ...mapActions('contentAccess', ['trackDownload']),
    capitalizeLinkType(linkType) {
      return linkType.charAt(0).toUpperCase() + linkType.slice(1)
    },
    formatLabelWithSet(label) {
      if (this.resource.set_number && this.resource.set_number !== 0) {
        // Insert " Set X" before the parentheses
        const parenIndex = label.lastIndexOf('(')
        if (parenIndex > 0) {
          const beforeParen = label.substring(0, parenIndex).trim()
          const afterParen = label.substring(parenIndex)
          return `${beforeParen} Set ${this.resource.set_number} ${afterParen}`
        } else {
          // No parentheses, just add to the end
          return `${label} Set ${this.resource.set_number}`
        }
      }
      return label
    },
    validateAndOpenDownload({ label, url }, isGoogleDrive) {
      let data = {
        download_url: url,
        label: label,
        is_google_drive_link: isGoogleDrive,
        isRedownload: this.contentAccessStatus.redownloadMap[this.resource.uuid] || false,
        resource_language: this.resource.language
      }
      if (this.resource['$_source']) {
        data.source = this.resource['$_source']
      }

      if (!this.downloadPermissions.block) {
        this.trackDownload({ resource: this.resource, link: url })
        this.$sendEvent('Opened Download Link', data, this.contentInfo)
        this.$emit('resourceDownloadOpened')
        window.open(url)
      }
    },
    contentBlocked() {
      this.$store.dispatch('contentAccess/sendContentAccessBlockedQuota', {
        actionType: ACTION_TYPES.DOWNLOAD,
        contentInfo: {
          cu: this.resource.uuid,
          ct: this.resource.title,
          ctype: CONTENT_TYPES.RESOURCE
        }
      })
    },
    trackDownloadsShown(event) {
      if (!event) {
        this.$sendEvent(
          'Clicked Download',
          {
            download_links: this.downloadLinks.map(link => link.label + '|' + link.url)
          },
          this.contentInfo
        )
        this.$emit('resourceDownloadClicked')
      }
    },
    trackGoogleLinksShown(event) {
      if (!event) {
        this.$sendEvent(
          'Clicked Download',
          {
            download_links: this.googleDriveLinks.map(link => link.label + '|' + link.url)
          },
          this.contentInfo
        )
        this.$emit('resourceDownloadClicked')
      }
    }
  },
  mounted() {
    this.id = this._uid
  }
}
</script>

<style lang="less" scoped>
@import '~bootstrap/less/mixins/buttons';
@import '~@/variables.less';
/deep/ .btn-gb-light {
  .button-variant(@gb_blue_medium, white, @gb_blue_medium);
  border-color: var(--blue-default);
  display: inline-flex;
  align-items: center;
  padding: 5.5px 16px;
}
/deep/ .open-in-drive-dropdown {
  width: max-content;
  max-width: 250px;
  white-space: normal;
}
.small-icons, .material-icons-round {
  font-size: 16px;
}
.large-icons {
  font-size: 32px;
}
/deep/ .download-btn {
  margin-right: 4px;
  background-color: var(--blue-default);
  display: inline-flex;
  padding: 8px 16px;
  align-items: center;
  line-height: normal;
  border: none;
  &:hover {
    background-color: var(--blue-hover);
  }
}
/deep/ .condense-buttons {
  padding: 3px;
}

.pr-1 {
  padding-right: 2px;
}
.pr-2 {
  padding-right: 3px;
}

</style>
